import { useQuery } from '@tanstack/react-query';
import { Link, NavLink } from 'react-router-dom';
import { Clock, Star, X, type LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import {
  NAV,
  STAFF_NAV,
  ROLE_LABEL,
  ROLE_COLOR,
  STATUS_LABEL,
  STATUS_COLOR,
} from '@/lib/constants';
import { cn } from '@/lib/utils';
import { formatNumber } from '@keywordgod/shared';

/** 데스크톱 사이드바 — 좌측 고정 / 모바일 슬라이드 오버레이 */
export function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const isStaff = !!user && (user.role === 'admin' || user.role === 'moderator');

  return (
    <>
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          'fixed md:sticky md:top-16 inset-y-0 left-0 z-50 md:z-0 w-72 md:w-64',
          'md:h-[calc(100vh-4rem)] h-screen md:translate-x-0',
          'border-r border-gray-800 bg-[#030712] md:bg-transparent',
          'transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
        aria-label="사이드바"
      >
        <div className="flex h-full flex-col overflow-y-auto p-4 gap-6">
          <div className="md:hidden flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-300 hover:bg-gray-900"
              aria-label="사이드바 닫기"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* 모바일 전용 네비 */}
          <nav className="md:hidden space-y-1">
            {NAV.map((n) => (
              <SidebarNavLink key={n.to} to={n.to} icon={n.icon} label={n.label} onClose={onClose} />
            ))}
            {isStaff && (
              <>
                <div className="mt-3 mb-1 text-[10px] uppercase tracking-widest text-gray-500 px-3">
                  운영진
                </div>
                {STAFF_NAV.filter(
                  (n) =>
                    !n.requireRole ||
                    user?.role === 'admin' ||
                    (n.requireRole !== 'admin' && user?.role === 'moderator'),
                ).map((n) => (
                  <SidebarNavLink
                    key={n.to}
                    to={n.to}
                    icon={n.icon}
                    label={n.label}
                    onClose={onClose}
                  />
                ))}
              </>
            )}
          </nav>

          <StatusCard />
          <HistoryList onItemClick={onClose} />
          <FavoritesList onItemClick={onClose} />
        </div>
      </aside>
    </>
  );
}

function SidebarNavLink({
  to,
  icon: Icon,
  label,
  onClose,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  onClose: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClose}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm',
          isActive ? 'bg-gold/10 text-gold font-semibold' : 'text-gray-300 hover:bg-gray-900',
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

function StatusCard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  return (
    <section className="rounded-xl border border-gray-800 bg-gray-900 p-4">
      <div className="text-xs uppercase tracking-widest text-gray-400">내 상태</div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', ROLE_COLOR[user.role])}>
          {ROLE_LABEL[user.role]}
        </span>
        <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_COLOR[user.status])}>
          {STATUS_LABEL[user.status]}
        </span>
      </div>
      {user.status === 'pending' && (
        <p className="mt-3 text-xs text-amber-300">
          운영진 승인 대기 중입니다. 챌린지 단톡방에 가입 알림을 남겨주세요.
        </p>
      )}
      {user.status === 'active' && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">오늘 사용량</div>
          <div className="tabular text-lg font-bold text-white mt-0.5">
            {formatNumber(user.dailyUsage)} 회
          </div>
        </div>
      )}
    </section>
  );
}

function HistoryList({ onItemClick }: { onItemClick: () => void }) {
  const user = useAuthStore((s) => s.user);
  const q = useQuery({
    queryKey: ['me', 'history'],
    queryFn: () => api.me.history(),
    enabled: !!user && user.status === 'active',
    staleTime: 60_000,
  });

  if (!user || user.status !== 'active') return null;

  return (
    <section>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400">
        <Clock className="h-3.5 w-3.5" />
        검색 히스토리
      </div>
      <div className="mt-2 space-y-1">
        {q.isLoading && (
          <>
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-7 w-5/6" />
          </>
        )}
        {q.data && q.data.length === 0 && (
          <p className="text-xs text-gray-500">아직 검색 이력이 없습니다</p>
        )}
        {q.data?.slice(0, 20).map((h) => {
          const failed = h.godScore == null && h.grade == null;
          return (
            <Link
              key={h.id}
              to={`/analyze/${encodeURIComponent(h.keyword)}`}
              onClick={onItemClick}
              className={cn(
                'flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-gray-900',
                failed ? 'text-gray-500' : 'text-gray-300',
              )}
              title={failed ? '분석 실패 — 다시 시도하려면 클릭' : undefined}
            >
              <span className="truncate">{h.keyword}</span>
              {h.grade ? (
                <span className="ml-2 shrink-0 text-[10px] font-bold text-gold">{h.grade}</span>
              ) : failed ? (
                <span className="ml-2 shrink-0 text-[10px] font-bold text-gray-600">실패</span>
              ) : null}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function FavoritesList({ onItemClick }: { onItemClick: () => void }) {
  const user = useAuthStore((s) => s.user);
  const q = useQuery({
    queryKey: ['me', 'favorites'],
    queryFn: () => api.me.favorites(),
    enabled: !!user && user.status === 'active',
    staleTime: 60_000,
  });
  if (!user || user.status !== 'active') return null;

  return (
    <section>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400">
        <Star className="h-3.5 w-3.5 text-gold" />
        즐겨찾기
      </div>
      <div className="mt-2 space-y-1">
        {q.isLoading && (
          <>
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-3/4" />
          </>
        )}
        {q.data && q.data.length === 0 && (
          <p className="text-xs text-gray-500">아직 즐겨찾기가 없습니다</p>
        )}
        {q.data?.map((f) => (
          <Link
            key={f.keyword}
            to={`/analyze/${encodeURIComponent(f.keyword)}`}
            onClick={onItemClick}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-300 hover:bg-gray-900"
          >
            <Star className="h-3 w-3 text-gold shrink-0" fill="currentColor" />
            <span className="truncate">{f.keyword}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
